import React, { useRef, useState } from 'react';
import {
  IconCheck,
  IconEye,
  IconEyeOff,
  IconLock,
  IconMail,
  IconPhone,
  IconRefresh,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { InputMask } from '@react-input/mask';
import { Controller, useForm } from 'react-hook-form';
import type { User, UserRole } from '../../../entities/user';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Select } from '../../../shared/ui/Select';
import { PHONE_MASK_OPTIONS } from '../../../shared/lib/phone';
import {
  EMPTY_USER_FORM,
  generatePassword,
  ROLE_OPTIONS,
  type UserForm,
} from '../model/types';

interface UserModalProps {
  mode: 'add' | 'edit';
  initial?: User | null;
  onClose: () => void;
  onSaved: (updated?: User) => void;
}

const UserModal: React.FC<UserModalProps> = ({ mode, initial, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<UserForm>({
    defaultValues:
      mode === 'edit' && initial
        ? {
            name: initial.name,
            email: initial.email,
            phone: initial.phone ?? '',
            password: '',
            role: (initial.role?.toUpperCase() as UserRole) || 'USER',
          }
        : EMPTY_USER_FORM,
  });

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      if (mode === 'add') {
       const user = await pb.collection('users').create({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          password: values.password,
          passwordConfirm: values.password,
          role: values.role,
          needResetPass: true,
          actual: true,
        });

        await pb.collection('users').update(user.id, {
          emailVisibility: true
        });
        logAction(`Created user: ${values.email.trim()}`, 'ADD');
      } else {
        const data: Record<string, unknown> = {
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          role: values.role,
          emailVisibility: true,
        };
        if (values.password.trim()) {
          data.password = values.password;
          data.passwordConfirm = values.password;
        }
        await pb.collection('users').update(initial!.id, data);
        logAction(`Edited user: ${values.email.trim()}`, 'EDIT');
        onSaved({
          id: initial!.id,
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim() || undefined,
          role: values.role,
          created: initial!.created,
        });
        onClose();
        return;
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 480, overflow: 'visible' }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Добавить пользователя' : 'Редактировать пользователя'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 14, overflow: 'visible' }}>
          <div className="adm-field">
            <label className="adm-label">Имя</label>
            <div className="adm-input-wrap">
              <IconUser size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input"
                type="text"
                placeholder="Иван Иванов"
                autoComplete="off"
                {...register('name')}
              />
            </div>
          </div>
          <div className="adm-field">
            <label className="adm-label">Телефон</label>
            <div className="adm-input-wrap">
              <IconPhone size={15} stroke={2} className="adm-input-icon" />
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <InputMask
                    className="adm-input"
                    mask={PHONE_MASK_OPTIONS.mask}
                    replacement={PHONE_MASK_OPTIONS.replacement}
                    placeholder="+7 (___) ___-__-__"
                    type="tel"
                    autoComplete="off"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Email <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconMail size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input"
                type="email"
                placeholder="user@example.com"
                autoComplete="off"
                {...register('email', {
                  required: 'Email обязателен',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Введите корректный email',
                  },
                })}
              />
            </div>
            {errors.email?.message ? <div className="form-field-error">{errors.email.message}</div> : null}
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Пароль{mode === 'add' && <span className="adm-req"> *</span>}
              {mode === 'edit' && <span className="adm-hint"> — оставьте пустым, чтобы не менять</span>}
            </label>
            <div className="adm-input-wrap">
              <IconLock size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input adm-input-has-suffix"
                type={showPwd ? 'text' : 'password'}
                placeholder={mode === 'add' ? 'Минимум 8 символов' : '••••••••'}
                autoComplete="new-password"
                {...register('password', {
                  validate: value => {
                    if (mode === 'add' && value.length < 8) return 'Пароль минимум 8 символов';
                    if (mode === 'edit' && value && value.length < 8) return 'Пароль минимум 8 символов';
                    return true;
                  },
                })}
              />
              <div className="adm-input-suffix">
                <button
                  type="button"
                  className="adm-pwd-btn"
                  onClick={() => setShowPwd(v => !v)}
                  title={showPwd ? 'Скрыть' : 'Показать'}
                >
                  {showPwd ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                </button>
                <button
                  type="button"
                  className="adm-pwd-btn adm-pwd-gen"
                  title="Сгенерировать пароль"
                  onClick={() => {
                    const p = generatePassword();
                    setValue('password', p, { shouldValidate: true });
                    setShowPwd(true);
                  }}
                >
                  <IconRefresh size={15} stroke={2} />
                </button>
              </div>
            </div>
            {errors.password?.message ? <div className="form-field-error">{errors.password.message}</div> : null}
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Роль <span className="adm-req">*</span>
            </label>
            <Controller
              control={control}
              name="role"
              rules={{ required: 'Выберите роль' }}
              render={({ field }) => (
                <Select
                  options={ROLE_OPTIONS}
                  value={field.value}
                  onChange={v => field.onChange(v as UserRole)}
                />
              )}
            />
            {errors.role?.message ? <div className="form-field-error">{errors.role.message}</div> : null}
          </div>
          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { UserModal };
